import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { PageContainer } from '../../../shared/components/PageContainer';
import { Textarea } from '../../../shared/components/Textarea';
import { useToast } from '../../../shared/components/Toast';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import { FEATURES } from '../../../shared/lib/permissions';
import { mensagemDeErro } from '../api';
import { AccessTask } from '../api/alocacoes';
import { ACCESS_TASK_KIND_LABELS, ACCESS_TASK_STATUS_LABELS, formatDate } from '../components/format';
import { PessoasModal } from '../components/PessoasModal';
import { useAgirNaTarefaDeAcesso, useTarefasDeAcesso } from '../hooks/alocacoes';

type Acao = 'concluir' | 'falha' | 'excecao';

const TITULOS: Record<Acao, string> = { concluir: 'Concluir tarefa', falha: 'Registrar falha', excecao: 'Autorizar exceção' };

/** Suspensão e reativação de acessos no cliente durante as férias (US-GP-015). */
export default function TarefasAcessoFeriasPage() {
  const toast = useToast();
  const { can } = usePermissions();
  const [scope, setScope] = useState<'abertas' | 'todas'>('abertas');
  const tarefas = useTarefasDeAcesso(scope);
  const agir = useAgirNaTarefaDeAcesso();
  const [acao, setAcao] = useState<{ task: AccessTask; action: Acao } | null>(null);
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);
  const podeAgir = can(FEATURES.pessoasFeriasAcessos, 'edit');

  async function submit() {
    if (!acao) return;
    if (acao.action !== 'concluir' && !notes.trim()) {
      return setError(acao.action === 'falha' ? 'Descreva a falha.' : 'Informe a justificativa da autorização.');
    }
    try {
      await agir.mutateAsync({ id: acao.task.id, action: acao.action, notes: notes.trim() || undefined });
      toast.success('Tarefa atualizada.');
      setAcao(null);
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível atualizar a tarefa.'));
    }
  }

  const lista = tarefas.data ?? [];
  return (
    <PageContainer title="Acessos nas férias" subtitle="Suspender e reativar os acessos aos clientes durante as férias dos profissionais alocados."
      breadcrumbs={[{ label: 'Pessoas' }, { label: 'Acessos nas férias' }]}>
      <div className="mb-4 flex gap-2">
        <Button size="sm" variant={scope === 'abertas' ? 'primary' : 'secondary'} onClick={() => setScope('abertas')}>Pendentes e falhas</Button>
        <Button size="sm" variant={scope === 'todas' ? 'primary' : 'secondary'} onClick={() => setScope('todas')}>Todas</Button>
      </div>
      {tarefas.isLoading ? (
        <p className="text-sm text-gray-500">Carregando…</p>
      ) : lista.length === 0 ? (
        <EmptyState title="Nenhuma tarefa" />
      ) : (
        <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
          {lista.map((t) => {
            const aberta = t.status === 'pendente' || t.status === 'falha';
            return (
              <li key={t.id} className="flex flex-wrap items-start justify-between gap-3 px-4 py-3 text-sm">
                <div className="space-y-0.5">
                  <p className="font-medium text-gray-900">
                    {ACCESS_TASK_KIND_LABELS[t.kind]} em {formatDate(t.dueDate)}
                    <span className={`ml-2 rounded px-1.5 py-0.5 text-xs ${t.status === 'falha' ? 'bg-red-100 text-red-800' : 'bg-gray-100 text-gray-700'}`}>
                      {ACCESS_TASK_STATUS_LABELS[t.status]}
                    </span>
                  </p>
                  <p className="text-gray-700">
                    <Link className="text-blue-700 hover:underline" to={`/pessoas/colaboradores/${t.userId}`}>{t.personName}</Link>
                    {' '}· {t.clientName}{t.projectName ? ` · ${t.projectName}` : ''} · férias de {formatDate(t.vacation.startDate)} a {formatDate(t.vacation.endDate)}
                  </p>
                  {t.accessNotes && <p className="text-xs text-gray-600">Acessos: {t.accessNotes}</p>}
                  {t.notes && <p className="text-xs text-gray-500">Nota: {t.notes}</p>}
                  {t.doneAt && <p className="text-xs text-gray-500">{t.doneByName ?? '—'} em {new Date(t.doneAt).toLocaleString('pt-BR')}</p>}
                </div>
                {aberta && podeAgir && (
                  <span className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => { setNotes(''); setError(null); setAcao({ task: t, action: 'concluir' }); }}>Concluir</Button>
                    <Button size="sm" variant="secondary" onClick={() => { setNotes(''); setError(null); setAcao({ task: t, action: 'falha' }); }}>Falha</Button>
                    {t.kind === 'suspensao' && (
                      <Button size="sm" variant="ghost" onClick={() => { setNotes(''); setError(null); setAcao({ task: t, action: 'excecao' }); }}>Exceção</Button>
                    )}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <PessoasModal open={acao !== null} title={acao ? TITULOS[acao.action] : ''} submitLabel="Confirmar" isSubmitting={agir.isPending}
        submitError={error} onClose={() => setAcao(null)} onSubmit={submit}
        description={acao?.action === 'excecao' ? 'Os acessos continuam ativos durante as férias. Registre quem autorizou e por quê.' : undefined}>
        <Textarea label={acao?.action === 'concluir' ? 'Observação (opcional)' : acao?.action === 'falha' ? 'Descrição da falha' : 'Justificativa da autorização'}
          value={notes} onChange={(e) => setNotes(e.target.value)} />
      </PessoasModal>
    </PageContainer>
  );
}
